import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import posthog from "posthog-js";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import {
	CourierDelivery,
	DeliveryStatusEnum,
} from "@/lib/types/delivery.types";
import { DeliveryStatusBadge } from "../job-page/job-page-components/deliveries/delivery-status-badge.component";
import { Button } from "@/dentlab/src/components/ui/button";
import { Textarea } from "@/dentlab/src/components/ui/textarea";
import {
	ArrowLeft,
	Check,
	MapPin,
	Upload,
	X as XIcon,
	Image as ImageIcon,
	Loader2,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { Logger } from "@/lib/logger/Logger";
import { uploadFileWithProvidedPath } from "@/dentlab/src/hooks/useStorage";
import { useFileUpload } from "@/dentlab/src/hooks/useFileUpload";
import { sanitizeFileName } from "@/dentlab/src/lib/utils/utils-functions";
import { FileViewerModal } from "@/components/src/file-viewer";

interface DeliveryFile {
	id: string;
	file_name: string;
	path_name: string;
	bucket_name: string;
	signedUrl?: string;
}

export const CourierDeliveryDetailPage: React.FC = () => {
	const { deliveryId } = useParams<{ deliveryId: string }>();
	const navigate = useNavigate();
	const { courierDeliveries, fetchCourierDeliveries, updateDeliveryStatus } =
		useJobStore((state) => ({
			courierDeliveries: state.courierDeliveries,
			fetchCourierDeliveries: state.fetchCourierDeliveries,
			updateDeliveryStatus: state.updateDeliveryStatus,
		}));

	const [note, setNote] = useState("");
	const [noteSaved, setNoteSaved] = useState(false);
	const [files, setFiles] = useState<DeliveryFile[]>([]);
	const [filesLoading, setFilesLoading] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
	const lastSavedNote = useRef("");
	const [fileViewerOpen, setFileViewerOpen] = useState(false);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

	const delivery = courierDeliveries.find((d) => d.id === Number(deliveryId));

	useEffect(() => {
		if (courierDeliveries.length === 0) {
			fetchCourierDeliveries();
		}
	}, []);

	useEffect(() => {
		if (delivery?.note != null) {
			setNote(delivery.note);
			lastSavedNote.current = delivery.note;
		}
	}, [delivery?.id]);

	useEffect(() => {
		if (delivery?.file_ids && delivery.file_ids.length > 0) {
			fetchDeliveryFiles();
		}
	}, [delivery?.id]);

	useEffect(() => {
		return () => {
			if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
		};
	}, []);

	const saveNote = async (value: string) => {
		if (!delivery || value === lastSavedNote.current) return;
		const { error } = await supabase
			.from("deliveries")
			.update({ note: value })
			.eq("id", delivery.id);

		if (error) {
			Logger.error(error, {}, "Error saving note");
		} else {
			lastSavedNote.current = value;
			setNoteSaved(true);
			setTimeout(() => setNoteSaved(false), 2000);
		}
	};

	const handleNoteChange = (value: string) => {
		setNote(value);
		setNoteSaved(false);
		if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
		saveTimerRef.current = setTimeout(() => saveNote(value), 800);
	};

	const fetchDeliveryFiles = async () => {
		if (!delivery) return;
		setFilesLoading(true);
		const { data, error } = await supabase.rpc("get_delivery_files", {
			p_delivery_id: delivery.id,
		});
		if (error) {
			Logger.error(error, {}, "Error fetching delivery files");
			setFilesLoading(false);
			return;
		}
		const fileData = (data || []) as unknown as DeliveryFile[];
		const withUrls = await Promise.all(
			fileData.map(async (f) => {
				const { data: urlData } = await supabase.storage
					.from(f.bucket_name)
					.createSignedUrl(f.path_name, 3600);
				return { ...f, signedUrl: urlData?.signedUrl };
			})
		);
		setFiles(withUrls);
		setFilesLoading(false);
	};

	const handleFileUpload = async (uploadFiles: File[]) => {
		if (!delivery) return;
		for (const file of uploadFiles) {
			const path = `deliveries/${
				delivery.id
			}/${Date.now()}_${sanitizeFileName(file.name)}`;
			const { success } = await uploadFileWithProvidedPath({
				filePath: path,
				fileBody: file,
				fileOptions: { contentType: file.type },
				bucketName: "v1",
			});
			if (!success) {
				showNotification({
					message: `Fehler beim Hochladen: ${file.name}`,
					type: "error",
				});
				continue;
			}
			const { error } = await supabase.rpc("upload_delivery_file", {
				p_delivery_id: delivery.id,
				p_file_name: file.name,
				p_path_name: path,
				p_bucket_name: "v1",
			});
			if (error) {
				Logger.error(error, {}, "Error registering delivery file");
				showNotification({
					message: `Fehler beim Registrieren: ${file.name}`,
					type: "error",
				});
			}
		}
		posthog.capture("delivery_file_uploaded", {
			file_count: uploadFiles.length,
			source: "courier",
		});
		showNotification({ message: "Dateien hochgeladen", type: "success" });
		await fetchDeliveryFiles();
		await fetchCourierDeliveries();
	};

	const { isDragging, isUploading, dragHandlers } = useFileUpload({
		onFileUpload: handleFileUpload,
	});

	const handleStatusChange = async (status: DeliveryStatusEnum) => {
		if (!delivery) return;
		await updateDeliveryStatus(delivery.id, status);
		await fetchCourierDeliveries();
	};

	const handleOpenFile = (fileId: string) => {
		setSelectedFileId(fileId);
		setFileViewerOpen(true);
	};

	const buildMapsUrl = (parts: (string | null | undefined)[]) => {
		const address = parts.filter(Boolean).join(", ");
		return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
			address
		)}`;
	};

	const formatDate = (dateStr: string | null) => {
		if (!dateStr) return null;
		return new Date(dateStr).toLocaleDateString("de-CH", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	if (!delivery) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-gray-500">Lieferung wird geladen...</div>
			</div>
		);
	}

	const statusActions = (() => {
		const actions: {
			status: DeliveryStatusEnum;
			label: string;
			variant: "default" | "outline";
		}[] = [];
		if (delivery.status === DeliveryStatusEnum.PENDING) {
			actions.push({
				status: DeliveryStatusEnum.IN_TRANSIT,
				label: "Als unterwegs markieren",
				variant: "default",
			});
			actions.push({
				status: DeliveryStatusEnum.DELIVERED,
				label: "Als zugestellt markieren",
				variant: "outline",
			});
		} else if (delivery.status === DeliveryStatusEnum.IN_TRANSIT) {
			actions.push({
				status: DeliveryStatusEnum.DELIVERED,
				label: "Als zugestellt markieren",
				variant: "default",
			});
		}
		return actions;
	})();
	const isFinal =
		delivery.status === DeliveryStatusEnum.DELIVERED ||
		delivery.status === DeliveryStatusEnum.FAILED;

	const fileIds =
		files.length > 0 ? files.map((f) => f.id) : delivery.file_ids || [];

	return (
		<>
			<div
				className="min-h-screen bg-gray-50"
				data-testid="courier-delivery-detail"
			>
				<header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
					<Button
						variant="ghost"
						size="sm"
						className="p-1"
						onClick={() => navigate("/")}
					>
						<ArrowLeft className="h-5 w-5" />
					</Button>
					<div>
						<span className="text-sm font-mono text-gray-500">
							{delivery.job_code}
						</span>
						<div className="flex items-center gap-2">
							<DeliveryStatusBadge status={delivery.status} />
						</div>
					</div>
				</header>

				<main className="max-w-lg mx-auto px-4 py-4 space-y-4">
					{!isFinal && (
						<div className="space-y-2">
							{statusActions.map((action) => (
								<Button
									key={action.status}
									variant={action.variant}
									className="w-full"
									onClick={() =>
										handleStatusChange(action.status)
									}
									data-testid={`courier-status-button-${action.status}`}
								>
									{action.label}
								</Button>
							))}
							<Button
								variant="outline"
								className="w-full text-red-500 border-red-200 hover:bg-red-50 hover:text-red-700"
								onClick={() =>
									handleStatusChange(
										DeliveryStatusEnum.FAILED
									)
								}
								data-testid={`courier-status-button-${DeliveryStatusEnum.FAILED}`}
							>
								<XIcon className="h-4 w-4 mr-2" />
								Fehlgeschlagen
							</Button>
						</div>
					)}

					{delivery.description && (
						<div className="bg-white rounded-lg border border-gray-200 p-3">
							<h3 className="text-xs font-medium text-gray-500 mb-1">
								Beschreibung
							</h3>
							<p className="text-sm text-gray-900">
								{delivery.description}
							</p>
						</div>
					)}

					{delivery.scheduled_departure_at && (
						<div className="bg-white rounded-lg border border-gray-200 p-3">
							<h3 className="text-xs font-medium text-gray-500 mb-1">
								Geplante Abfahrt
							</h3>
							<p className="text-sm text-gray-900">
								{formatDate(delivery.scheduled_departure_at)}
							</p>
						</div>
					)}

					{delivery.delivered_at && (
						<div className="bg-white rounded-lg border border-gray-200 p-3">
							<h3 className="text-xs font-medium text-gray-500 mb-1">
								Zugestellt
							</h3>
							<p className="text-sm text-green-700">
								{formatDate(delivery.delivered_at)}
							</p>
						</div>
					)}

					<div className="bg-white rounded-lg border border-gray-200 p-3">
						<div className="flex items-center justify-between mb-1">
							<h3 className="text-xs font-medium text-gray-500">
								Notiz
							</h3>
							{noteSaved && (
								<span className="flex items-center gap-1 text-xs text-green-600">
									<Check className="h-3 w-3" />
									Gespeichert
								</span>
							)}
						</div>
						<Textarea
							value={note}
							onChange={(e) => handleNoteChange(e.target.value)}
							rows={3}
							placeholder="Notiz hinzufügen..."
							data-testid="courier-note-input"
						/>
					</div>

					<div
						className="bg-white rounded-lg border border-gray-200 p-3"
						data-testid="courier-files-section"
					>
						<h3 className="text-xs font-medium text-gray-500 mb-2">
							Fotos / Dateien
						</h3>
						{files.length > 0 && (
							<div className="grid grid-cols-3 gap-2 mb-3">
								{files.map((f) => (
									<button
										key={f.id}
										type="button"
										onClick={() => handleOpenFile(f.id)}
										className="block aspect-square rounded-md overflow-hidden border border-gray-100 hover:border-gray-300 transition-colors bg-gray-50 cursor-pointer"
										aria-label={`Datei anzeigen: ${f.file_name}`}
										data-testid={`courier-file-${f.id}`}
									>
										{f.signedUrl &&
										/\.(jpg|jpeg|png|gif|webp|heic)$/i.test(
											f.file_name
										) ? (
											<img
												src={f.signedUrl}
												alt={f.file_name}
												className="w-full h-full object-cover"
											/>
										) : (
											<div className="w-full h-full flex flex-col items-center justify-center p-1">
												<ImageIcon className="h-6 w-6 text-gray-400 mb-1" />
												<span className="text-[10px] text-gray-500 truncate w-full text-center">
													{f.file_name}
												</span>
											</div>
										)}
									</button>
								))}
							</div>
						)}
						{filesLoading && (
							<div className="flex items-center justify-center py-3">
								<Loader2 className="h-4 w-4 animate-spin text-gray-400" />
							</div>
						)}
						<div
							{...dragHandlers}
							className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
								isDragging
									? "border-blue-400 bg-blue-50"
									: "border-gray-200 hover:border-gray-300"
							}`}
							onClick={() => fileInputRef.current?.click()}
							data-testid="courier-file-upload-zone"
						>
							<input
								ref={fileInputRef}
								type="file"
								multiple
								accept="image/*,application/pdf"
								className="hidden"
								onChange={(e) => {
									if (e.target.files?.length) {
										handleFileUpload(
											Array.from(e.target.files)
										);
										e.target.value = "";
									}
								}}
							/>
							{isUploading ? (
								<div className="flex items-center justify-center gap-2">
									<Loader2 className="h-4 w-4 animate-spin text-gray-500" />
									<span className="text-sm text-gray-500">
										Hochladen...
									</span>
								</div>
							) : (
								<div className="flex flex-col items-center gap-1">
									<Upload className="h-5 w-5 text-gray-400" />
									<span className="text-xs text-gray-500">
										Fotos oder Dateien hochladen
									</span>
								</div>
							)}
						</div>
					</div>

					{(delivery.lab_name || delivery.lab_street) && (
						<a
							href={buildMapsUrl([
								delivery.lab_name,
								delivery.lab_street,
								delivery.lab_postal_code,
								delivery.lab_city,
								delivery.lab_country,
							])}
							target="_blank"
							rel="noopener noreferrer"
							className="block bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-300 transition-colors"
							data-testid="courier-lab-address-link"
						>
							<div className="flex items-start gap-2">
								<MapPin className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
								<div>
									<h3 className="text-xs font-medium text-gray-500 mb-0.5">
										Labor
									</h3>
									<p className="text-sm font-medium text-gray-900">
										{delivery.lab_name}
									</p>
									{delivery.lab_street && (
										<p className="text-sm text-gray-600">
											{delivery.lab_street}
										</p>
									)}
									{(delivery.lab_postal_code ||
										delivery.lab_city) && (
										<p className="text-sm text-gray-600">
											{[
												delivery.lab_postal_code,
												delivery.lab_city,
											]
												.filter(Boolean)
												.join(" ")}
										</p>
									)}
								</div>
							</div>
						</a>
					)}

					{(delivery.client_name || delivery.client_street) && (
						<a
							href={buildMapsUrl([
								delivery.client_name,
								delivery.client_street,
								delivery.client_postal_code,
								delivery.client_city,
								delivery.client_country,
							])}
							target="_blank"
							rel="noopener noreferrer"
							className="block bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-300 transition-colors"
							data-testid="courier-client-address-link"
						>
							<div className="flex items-start gap-2">
								<MapPin className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
								<div>
									<h3 className="text-xs font-medium text-gray-500 mb-0.5">
										Kunde
									</h3>
									<p className="text-sm font-medium text-gray-900">
										{delivery.client_name}
									</p>
									{delivery.client_street && (
										<p className="text-sm text-gray-600">
											{delivery.client_street}
										</p>
									)}
									{(delivery.client_postal_code ||
										delivery.client_city) && (
										<p className="text-sm text-gray-600">
											{[
												delivery.client_postal_code,
												delivery.client_city,
											]
												.filter(Boolean)
												.join(" ")}
										</p>
									)}
								</div>
							</div>
						</a>
					)}
				</main>
			</div>
			{fileIds.length > 0 && (
				<FileViewerModal
					fileIds={fileIds}
					isOpen={fileViewerOpen}
					onClose={() => {
						setFileViewerOpen(false);
						setSelectedFileId(null);
					}}
					defaultFileId={selectedFileId || undefined}
				/>
			)}
		</>
	);
};
